/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { GuestCounter, Select, Label, NumberField } from 'sarsaparilla';

import DetailedAvailabilityFilterItem from '../../../shared-components/detailed-availability/DetailedAvailabilityFilterItem';
import {
    DEFAULT_MIN_GROUP_SIZE,
    DEFAULT_MAX_GROUP_SIZE,
} from '../../../shared-components/constants/rules';

import * as types from '../../constants';
import WatercraftAvailabilityFilter from './WatercraftAvailabilityFilter';

const guidesQuotaFilter = {
    label: 'Guides',
    type: 'guides',
};

export default class AvailabilityFilters extends Component {
    static propTypes = {
        permitID: PropTypes.string,
        equipments: PropTypes.array,
        quotaFilters: PropTypes.array,
        selectedQuotaToDisplay: PropTypes.string,
        quotaFilterValues: PropTypes.object,
        updateQuotaFilterValue: PropTypes.func,
        updateQuotaFilterType: PropTypes.func,
        role: PropTypes.string,
        guidesCountTowardsGroupSize: PropTypes.bool,
        isLabelVisible: PropTypes.bool,
        isBypassRulesValidations: PropTypes.bool,
        isMobileApp: PropTypes.bool,
        isGuestCounterDisabled: PropTypes.bool,
    };

    static defaultProps = {
        isLabelVisible: false,
    };

    handleViewQuotaByRadioGroupChange = (e) => {
        const radioValue = e.target.value;
        this.props.updateQuotaFilterType(radioValue, { selected: true });
    };

    handleCountChange = (newValue, type) => {
        this.props.updateQuotaFilterValue(type, { value: newValue });
    };

    render() {
        const {
            quotaFilters,
            guidesCountTowardsGroupSize,
            isLabelVisible,
            isBypassRulesValidations,
            isMobileApp,
        } = this.props;

        if (
            quotaFilters.length === 0 ||
            (quotaFilters.length === 1 && !quotaFilters[0].showStandalone)
        ) {
            return null;
        }

        const renderedFilters = [];
        const quotaViewOptions = [];

        quotaFilters.forEach((quotaFilter) => {
            // for now if quotaFilter is specifically QuotaUsageByMemberDaily
            const guidesCountTowardsQuota =
                quotaFilter.type === types.MEMBER_AVAILABILITY_DAILY_KEY_NAME &&
                guidesCountTowardsGroupSize;

            if (
                quotaFilter.display === types.FILTER_WATERCRAFT &&
                !quotaFilter.doNotRender
            ) {
                renderedFilters.push(
                    <DetailedAvailabilityFilterItem
                        id={quotaFilter.type}
                        key={quotaFilter.type}
                    >
                        <WatercraftAvailabilityFilter
                            permitID={this.props.permitID}
                            equipments={this.props.equipments}
                            label={quotaFilter.labelAlt}
                            minValue={quotaFilter.minValue}
                            maxValue={quotaFilter.maxValue}
                            maxEquipmentCount={quotaFilter.maxEquipmentCount}
                            onChange={(newValues) => {
                                this.props.updateQuotaFilterValue(
                                    quotaFilter.type,
                                    newValues
                                );
                            }}
                            renderVisibility={false}
                            inline={!isMobileApp}
                        />
                    </DetailedAvailabilityFilterItem>
                );
            } else if (
                quotaFilter.display === types.FILTER_NUMERICAL &&
                !quotaFilter.doNotRender
            ) {
                const guestTypes = [
                    {
                        key: quotaFilter.type,
                        type: quotaFilter.label,
                        description: '',
                    },
                ];
                const value = {
                    [quotaFilter.type]:
                        this.props.quotaFilterValues[quotaFilter.type] || 0,
                };
                const minimumCountByGuestType = {
                    [quotaFilter.type]: quotaFilter.minValue,
                };

                // might be better to move this outside forEach loop if causes problem in future
                if (guidesCountTowardsQuota) {
                    guestTypes.push({
                        key: guidesQuotaFilter.type,
                        type: guidesQuotaFilter.label,
                        description: '',
                    });
                    value[guidesQuotaFilter.type] =
                        this.props.quotaFilterValues[guidesQuotaFilter.type] || 0;
                }

                const maxTotalGuests = isBypassRulesValidations
                    ? DEFAULT_MAX_GROUP_SIZE
                    : quotaFilter.maxValue;
                const minTotalGuests = isBypassRulesValidations
                    ? DEFAULT_MIN_GROUP_SIZE
                    : quotaFilter.minValue;
                const guestCounterProps = {
                    title: maxTotalGuests
                        ? `${quotaFilter.labelAlt} (${maxTotalGuests} max)`
                        : quotaFilter.labelAlt,
                    minTotalGuests: this.props.isGuestCounterDisabled
                        ? null
                        : minTotalGuests,
                    maxTotalGuests,
                    onCountChange: (newValues) => {
                        Object.keys(value).forEach((type) => {
                            if (value[type] !== newValues[type]) {
                                this.props.updateQuotaFilterValue(type, {
                                    value: newValues[type],
                                });
                            }
                        });
                    },
                    minimumCountByGuestType,
                    guestTypes,
                    value,
                };

                renderedFilters.push(
                    <DetailedAvailabilityFilterItem
                        id={quotaFilter.type}
                        key={quotaFilter.type}
                        mobileLabel={quotaFilter.labelAlt}
                    >
                        {isMobileApp ? (
                            <NumberField
                                id="guest-counter"
                                units="members"
                                value={value[quotaFilter.type]}
                                min={quotaFilter.minValue}
                                max={quotaFilter.maxValue}
                                onChange={(newValue) =>
                                    this.handleCountChange(newValue, quotaFilter.type)
                                }
                            />
                        ) : (
                            <>
                                <Label
                                    htmlFor={`guest-counter-${quotaFilter.type}`}
                                    isVisible={isLabelVisible}
                                >
                                    Select Guests
                                </Label>
                                <GuestCounter
                                    id={`guest-counter-${quotaFilter.type}`}
                                    {...guestCounterProps}
                                />
                            </>
                        )}
                    </DetailedAvailabilityFilterItem>
                );
            }

            quotaViewOptions.push({
                value: quotaFilter.type,
                label: quotaFilter.label,
            });
        });

        return (
            <>
                {renderedFilters}
                {quotaViewOptions.length > 1 && (
                    <DetailedAvailabilityFilterItem
                        id="quota-view-selector"
                        mobileLabel="Select Quota View"
                    >
                        <Select
                            id="quota-view-selector"
                            label="Quota View"
                            isLabelVisible={isLabelVisible}
                            value={this.props.selectedQuotaToDisplay}
                            onChange={this.handleViewQuotaByRadioGroupChange}
                            placeholder="Quota View"
                            options={quotaViewOptions}
                        />
                    </DetailedAvailabilityFilterItem>
                )}
            </>
        );
    }
}
